import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { addPayrollLine, generatePayroll, getMyPayrolls, getPayrollById, getPayrolls, updatePayrollStatus } from "../../../services/payrollService";
import type { Payroll, PayrollGeneratePayload, PayrollLinePayload } from "../../../types/payrollTypes";

export const usePayroll = () => {
    const { t } = useTranslation();

    const [payrolls, setPayrolls] = useState<Payroll[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [generating, setGenerating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<any>({});
    const statusOptions = [
        { value: "coming", label: t("payroll.statusPayroll.coming") },
        { value: "overdue", label: t("payroll.statusPayroll.overdue") },
        { value: "paid", label: t("payroll.statusPayroll.paid") },
        { value: "partially_paid", label: t("payroll.statusPayroll.partially_paid") },
        { value: "failed", label: t("payroll.statusPayroll.failed") },
    ];

    const fetchPayrolls = useCallback(async (filters: any = { page: 1, limit: 10 }) => {
        try {
            setLoading(true);
            const res = await getPayrolls(filters);
            console.log("res", res);
            setPayrolls(res?.data ?? []);
            setPagination(res?.pagination ?? {});
            setError(null);
        } catch (err) {
           return false;
        } finally {
            setLoading(false);
        }
    }, [t]);

    const fetchMyPayrolls = useCallback(async (filters: any = { page: 1, limit: 10 }) => {
        try {
            setLoading(true);
            const res = await getMyPayrolls(filters);
            console.log("res", res);
            setPayrolls(res?.data ?? []);
            setPagination(res?.pagination ?? {});
            setError(null);
        } catch (err) {
           return false;
        } finally {
            setLoading(false);
        }
    }, [t]);

    const handleGeneratePayroll = async (payload: PayrollGeneratePayload) => {
        try {
            setGenerating(true);
            const res = await generatePayroll(payload);
            message.success(t("payroll.payroll_generated_successfully", { count: res?.data?.length }));
            await fetchPayrolls();
            return true;
        } catch (err: any) {
            return false;
        } finally {
            setGenerating(false);
        }
    };

    const handleUpdateStatus = async (payrollId: string, status: string) => {
        try {
            await updatePayrollStatus(payrollId, status);
            message.success(t("payroll.payroll_status_updated"));
            await fetchPayrolls();
            return true;
        } catch (err: any) {
            return false;
        }
    };

    const payrollDetail = async (payrollId: string) => {
        try {
            setLoading(true);
            const res = await getPayrollById(payrollId);
            return res?.data;
        } catch (err) {
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleAddPayrollLine = async (payload: PayrollLinePayload) => {
        try {
            await addPayrollLine(payload);
            message.success(t("payroll.payroll_line_added_successfully"));
            return true;
        } catch (err: any) {
            return false;
        }
    };

    return {
        payrolls,
        loading,
        generating,
        error,
        pagination,
        statusOptions,
        fetchPayrolls,
        handleGeneratePayroll,
        handleUpdateStatus,
        payrollDetail,
        handleAddPayrollLine,
        fetchMyPayrolls
    };
};

export default usePayroll;